// The map module's interface. Screens use this; only ./google touches the
// Google Maps API, so another provider (or a native map in Capacitor) can
// replace it without screen changes.
import type { PlaceLevel, Viewport } from '@guildite/domain/delivery';
import type { LatLng } from '@guildite/domain/location';
import type { Place } from '@guildite/domain/places';

// Area circles are drawn around the ~500 m grid point, so the circle always
// contains the real spot without revealing where in it the spot is.
export const AREA_CIRCLE_RADIUS_METERS = 500;

export type MapMarker =
  // draggable: dragging the pin chooses a point, like a tap (onPick).
  | { kind: 'pin'; point: LatLng; label: string; draggable?: boolean }
  | { kind: 'area'; center: LatLng; label: string }
  // Delivery distances: one circle per radius around the shop.
  | { kind: 'rings'; center: LatLng; radii: number[]; label: string };

export type MapItem = { id: string; marker: MapMarker };
// placeId and level are set for areas (towns, regions…) from searchPlaces.
// A town suggestion; `point` is only for moving the map there, never stored.
export type TownResult = Place & { label: string; point: LatLng };
export type PlaceResult = { name: string; address: string; point: LatLng; placeId?: string; level?: PlaceLevel | null; viewport?: Viewport | null };

// Delivery areas drawn for editing (DeliveryAreasEditor): circles the user
// drags and resizes, highlighted places and the shop's pin.
export type MapShape =
  | { id: string; kind: 'circle'; center: LatLng; radius: number; label: string; editable: boolean; selected: boolean }
  | { id: string; kind: 'place'; placeId: string | null; level: PlaceLevel; center: LatLng | null; viewport: Viewport | null; label: string; selected: boolean }
  | { id: string; kind: 'pin'; point: LatLng; label: string };

export type ShapeEvents = {
  onCircleChange?: (id: string, center: LatLng, radius: number) => void;
  onSelect?: (id: string) => void;
};

export type MapHandle = {
  show(marker: MapMarker | null): void;
  // Many markers at once (the /map screen); a tap on one calls onSelect with its id.
  showMany(items: MapItem[], onSelect: (id: string) => void): void;
  // Draws shapes, keeping ones already on the map (so a drag is not lost);
  // `fit` frames them all.
  showShapes(shapes: MapShape[], events: ShapeEvents, options?: { fit?: boolean }): void;
  // Move the view, for example to a searched place or the device's position.
  focus(point: LatLng, zoom?: number): void;
  // Zoom in (+1) or out (-1), for the screen's own zoom buttons.
  zoomBy(delta: number): void;
  // Tap or click on the map to choose a point. Returns an unsubscribe function.
  onPick(listener: (point: LatLng) => void): () => void;
  // The point at the centre of the view, so keyboard users can pan with the
  // arrow keys and then pin that spot.
  center(): LatLng | null;
  destroy(): void;
};

// `controls: false` hides the provider's zoom buttons when the screen draws its own.
// onFail: called if the provider gives up after the map was made (for
// example Google refusing the site's key), so the screen can say so.
// Where a map opens when there is nothing to show yet: a wide, country-level
// view the person can pan and zoom from (no town is assumed).
export const WIDE_VIEW = { center: { lat: 7.95, lng: -1.03 }, zoom: 6 };

export type MountOptions = { center: LatLng; zoom: number; interactive: boolean; label: string; colorScheme: 'light' | 'dark'; controls?: boolean; onFail?: () => void };

export interface MapsPort {
  readonly available: boolean;
  mount(element: HTMLElement, options: MountOptions): Promise<MapHandle>;
  directionsUrl(point: LatLng): string;
  // Places search (Places API, New), nearest first when `near` is given.
  // areasOnly keeps neighbourhoods, towns, districts, regions and countries.
  searchPlaces(query: string, near?: LatLng | null, options?: { areasOnly?: boolean }): Promise<PlaceResult[]>;
  // A short area name for a point ("Adum, Kumasi"), from reverse geocoding.
  placeName(point: LatLng): Promise<string | null>;
  // The town and country a point is in ("Use my current location"). The
  // caller drops the point; only the town and country are kept.
  townAt(point: LatLng): Promise<Place | null>;
  // Towns and cities whose name starts like the text, for suggestions.
  findTowns(query: string): Promise<TownResult[]>;
}

// A plain link that opens turn-by-turn directions in the device's maps app or
// browser; it needs no API key.
export function directionsUrl(point: LatLng): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${point.lat},${point.lng}`;
}

export const unavailableMaps: MapsPort = {
  available: false,
  mount: () => Promise.reject(new Error('Maps are not configured.')),
  directionsUrl,
  searchPlaces: () => Promise.reject(new Error('Maps are not configured.')),
  placeName: async () => null,
  townAt: async () => null,
  findTowns: async () => [],
};

// The area part of a geocoder result: neighbourhood (or sublocality) and town.
export function areaFromComponents(components: { types: string[]; name: string }[]): string | null {
  const find = (...types: string[]) => components.find(component => types.some(type => component.types.includes(type)))?.name;
  const parts = [find('neighborhood', 'sublocality_level_1', 'sublocality'), find('locality', 'administrative_area_level_2')].filter(Boolean) as string[];
  const unique = parts.filter((part, index) => parts.indexOf(part) === index);
  return unique.length ? unique.join(', ') : null;
}

// Town and country code from geocoder address parts: the locality (or the
// postal town or district when a place has no locality).
export function townFromComponents(components: { types: string[]; name: string; short: string }[]): Place | null {
  const find = (...types: string[]) => components.find(component => types.some(type => component.types.includes(type)));
  const town = find('locality', 'postal_town', 'administrative_area_level_2')?.name;
  const country = find('country')?.short;
  return town && country && /^[A-Z]{2}$/.test(country) ? { town, country } : null;
}
